import { NgStyle } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';

import { ProfileSwitcherService } from '@core/services/profile-switcher.service';

import { AvatarSize, type AvatarConfig } from '@shared/components/avatar/avatar.types';
import type { DynamicTextConfig } from '@shared/components/dynamic-text/dynamic-text.types';
import { Dynamic, type DynamicAction } from '@shared/registry';
import { getInitials } from '@shared/utils/string-utils';

import { type ProfileSelectorConfig, type ProfileSelectorSize } from './profile-selector.types';

const PROFILE_TO_AVATAR_SIZE: Record<ProfileSelectorSize, AvatarSize> = {
  sm: AvatarSize.SM,
  lg: AvatarSize.LG,
};

const ICON_SIZE: Record<ProfileSelectorSize, string> = {
  sm: '16',
  lg: '24',
};

@Component({
  selector: 'app-profile-selector',
  templateUrl: './profile-selector.html',
  imports: [NgStyle, Dynamic],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProfileSelector {
  private readonly profileSwitcherService = inject(ProfileSwitcherService);

  readonly config = input.required<ProfileSelectorConfig>();

  readonly action = output<DynamicAction | undefined>();

  // Derive state from ProfileSwitcherService.isOpen
  protected readonly state = computed(() => (this.profileSwitcherService.isOpen() ? 'active' : 'initial'));

  protected readonly iconSize = computed(() => ICON_SIZE[this.config().size]);

  protected readonly buttonStyle = computed(() => ({
    gap: 'var(--profile-selector-gap)',
    padding: `var(--profile-selector-${this.config().size}-padding-y) var(--profile-selector-${this.config().size}-padding-x-end) var(--profile-selector-${this.config().size}-padding-y) var(--profile-selector-${this.config().size}-padding-x-start)`,
    borderRadius: `var(--profile-selector-${this.config().size}-border-radius)`,
    borderColor: `var(--profile-selector-${this.config().size}-border-color)`,
    background: `var(--profile-selector-${this.config().size}-${this.state()}-background)`,
    fontFamily: 'var(--profile-selector-font-family)',
  }));

  protected readonly textConfig = computed<DynamicTextConfig>(() => ({
    text: this.config().name,
    color: `var(--profile-selector-${this.config().size}-${this.state()}-color)`,
    size: this.config().size === 'sm' ? 'xs' : 'base',
    fontWeight: this.config().size === 'sm' ? 'bold' : 'semibold',
    fontFamily: 'sans',
  }));

  protected readonly iconColor = computed(
    () => `var(--profile-selector-${this.config().size}-${this.state()}-icon-color)`,
  );

  protected readonly avatarConfig = computed<AvatarConfig>(() => ({
    size: PROFILE_TO_AVATAR_SIZE[this.config().size],
    imageUrl: this.config().avatarUrl,
    profileId: this.config().profileId,
    initials: this.config().avatarUrl ? undefined : this.config().initials || getInitials(this.config().name),
  }));

  protected onClick(): void {
    this.action.emit(this.config().action);
  }
}
